import React, { useEffect, useMemo, useRef, useState } from "react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";

/**
 * Vintage cartographic world map - looks like a page torn from a 19th
 * century shipping atlas.
 *
 * Visual decisions:
 *   - Land tile: parchment fill (#F0E6CC) with hand-drawn-style
 *     stippled sepia border (filter applied via SVG turbulence).
 *   - Sea: bone with very subtle dotted longitude lines.
 *   - Trade routes: bowed quadratic curves, dashed in vermilion-on-bone.
 *   - HQ (Mundra): wax-seal-style mark with brass concentric ring.
 *   - Ports: small filled vermilion squares (not circles - looks more
 *     like an actual cartographic legend).
 *   - Compass rose top-right, scale bar bottom-left, edition stamp
 *     bottom-right. The whole frame has a folio border.
 */

// Real Mundra Port coordinates: 22.84°N, 69.72°E (Kutch, Gujarat).
// Earlier value of 71.4°E placed the pin ~190 km east of the real port.
const HQ = { name: "Mundra · Gujarat", lat: 22.84, lon: 69.72 };

// Working trade lanes - only the ports the house actually serves.
// Coordinates are real-world to within ~0.1°.
const PORTS = [
  // Persian Gulf
  { name: "Jebel Ali · UAE",       lat: 25.01,  lon: 55.13,  region: "gulf"     },
  { name: "Doha · Qatar",          lat: 25.29,  lon: 51.53,  region: "gulf"     },
  { name: "Dammam · KSA",          lat: 26.43,  lon: 50.10,  region: "gulf"     },
  { name: "Muscat · Oman",         lat: 23.61,  lon: 58.55,  region: "gulf"     },
  // Africa & Levant
  { name: "Mombasa · Kenya",       lat: -4.04,  lon: 39.66,  region: "africa"   },
  { name: "Dar es Salaam",         lat: -6.79,  lon: 39.21,  region: "africa"   },
  { name: "Beirut · Lebanon",      lat: 33.89,  lon: 35.50,  region: "africa"   },
  { name: "Aqaba · Jordan",        lat: 29.53,  lon: 35.00,  region: "africa"   },
  // Asia Pacific
  { name: "Malé · Maldives",       lat: 4.17,   lon: 73.51,  region: "asia"     },
  { name: "Colombo · Sri Lanka",   lat: 6.93,   lon: 79.85,  region: "asia"     },
  { name: "Singapore",             lat: 1.29,   lon: 103.85, region: "asia"     },
  { name: "Yangon · Myanmar",      lat: 16.87,  lon: 96.19,  region: "asia"     },
  // Europe
  { name: "Rotterdam · NL",        lat: 51.95,  lon: 4.14,   region: "europe"   },
  { name: "Hamburg · DE",          lat: 53.55,  lon: 9.99,   region: "europe"   },
  { name: "Felixstowe · UK",       lat: 51.96,  lon: 1.35,   region: "europe"   },
  { name: "Genoa · IT",            lat: 44.40,  lon: 8.93,   region: "europe"   },
  // Americas
  { name: "New York · USA",        lat: 40.66,  lon: -74.18, region: "americas" },
  { name: "Houston · USA",         lat: 29.76,  lon: -95.36, region: "americas" },
  { name: "Vancouver · CA",        lat: 49.29,  lon: -123.10,region: "americas" },
  { name: "Santos · BR",           lat: -23.95, lon: -46.33, region: "americas" },
];

const REGION_INK = {
  gulf:     "#C8431F", // vermilion
  africa:   "#A8843D", // brass
  asia:     "#1A1F36", // ink
  europe:   "#5B7553", // forest olive
  americas: "#3F5E8C", // navy slate
};

// Label offsets push labels into clear ocean/empty space relative to each
// pin. Anchors chosen so leader lines run from the pin toward the label
// edge nearest the pin. Tightly-clustered Persian Gulf ports (Dammam, Doha,
// Jebel Ali, Muscat) get distinct directions to avoid label collision.
const PORT_LABELS = {
  // Persian Gulf - Mundra HQ is just east of Muscat (~30 px apart at scale).
  // ALL Gulf labels point WEST or DOWN to avoid colliding with Mundra's
  // pulsing wax-seal ring. Each port gets a different direction so labels
  // don't pile up.
  "Dammam · KSA": {
    // Far north-west into the Saudi interior - northernmost label
    lines: ["DAMMAM"],
    desktop: { dx: -32, dy: -22, anchor: "end" },
    mobile:  { dx: -28, dy: -22, anchor: "end" },
  },
  "Doha · Qatar": {
    // Straight up (above the Qatar peninsula), pushed far enough that it
    // clears the Mundra HQ caption to its east.
    lines: ["DOHA"],
    desktop: { dx: 0, dy: -28, anchor: "middle" },
    mobile:  { dx: 0, dy: -26, anchor: "middle" },
  },
  "Jebel Ali · UAE": {
    // Down-left (SW) so the label runs into UAE coast - keeps it well
    // away from Mundra HQ and from Muscat below.
    lines: ["JEBEL ALI"],
    desktop: { dx: -12, dy: 22, anchor: "end" },
    mobile:  { dx: -10, dy: 22, anchor: "end" },
  },
  "Muscat · Oman": {
    // Straight DOWN into the Arabian Sea - the only safe direction for
    // Muscat since east points right at Mundra.
    lines: ["MUSCAT"],
    desktop: { dx: -8, dy: 24, anchor: "middle" },
    mobile:  { dx: -6, dy: 24, anchor: "middle" },
  },

  // Africa & Levant
  "Beirut · Lebanon": {
    // West into eastern Mediterranean
    lines: ["BEIRUT"],
    desktop: { dx: -28, dy: -8, anchor: "end" },
    mobile:  { dx: -24, dy: -8, anchor: "end" },
  },
  "Aqaba · Jordan": {
    // South-west into Red Sea (away from Beirut to its N)
    lines: ["AQABA"],
    desktop: { dx: -28, dy: 14, anchor: "end" },
    mobile:  { dx: -24, dy: 14, anchor: "end" },
  },
  "Mombasa · Kenya": {
    // East into Indian Ocean
    lines: ["MOMBASA"],
    desktop: { dx: 28, dy: 0, anchor: "start" },
    mobile:  { dx: 24, dy: 2, anchor: "start" },
  },
  "Dar es Salaam": {
    // South-east into Indian Ocean (away from Mombasa to N)
    lines: ["DAR ES SALAAM"],
    desktop: { dx: 28, dy: 18, anchor: "start" },
    mobile:  { dx: 24, dy: 20, anchor: "start" },
  },

  // Asia Pacific
  "Malé · Maldives": {
    // West into Indian Ocean (away from Colombo to NE)
    lines: ["MALÉ"],
    desktop: { dx: -22, dy: 12, anchor: "end" },
    mobile:  { dx: -20, dy: 14, anchor: "end" },
  },
  "Colombo · Sri Lanka": {
    // South-east into Indian Ocean
    lines: ["COLOMBO"],
    desktop: { dx: 24, dy: 14, anchor: "start" },
    mobile:  { dx: 22, dy: 16, anchor: "start" },
  },
  "Yangon · Myanmar": {
    // North-east into Andaman Sea / land area (legibility on land OK here)
    lines: ["YANGON"],
    desktop: { dx: 26, dy: -8, anchor: "start" },
    mobile:  { dx: 22, dy: -8, anchor: "start" },
  },
  "Singapore": {
    // East into South China Sea
    lines: ["SINGAPORE"],
    desktop: { dx: 26, dy: 14, anchor: "start" },
    mobile:  { dx: 24, dy: 16, anchor: "start" },
  },

  // Europe - Rotterdam / Hamburg / Felixstowe sit close together on the
  // North Sea. Fan them in 3 different directions so labels don't pile up.
  "Rotterdam · NL": {
    lines: ["ROTTERDAM"],
    desktop: { dx: -22, dy: 14, anchor: "end" },     // SW into Channel
    mobile:  { dx: -20, dy: 14, anchor: "end" },
  },
  "Hamburg · DE": {
    lines: ["HAMBURG"],
    desktop: { dx: 22, dy: -8, anchor: "start" },    // E into mainland
    mobile:  { dx: 20, dy: -8, anchor: "start" },
  },
  "Felixstowe · UK": {
    lines: ["FELIXSTOWE"],
    desktop: { dx: -22, dy: -14, anchor: "end" },    // NW into UK
    mobile:  { dx: -20, dy: -14, anchor: "end" },
  },
  "Genoa · IT": {
    lines: ["GENOA"],
    desktop: { dx: -16, dy: 16, anchor: "end" },     // SW into Med
    mobile:  { dx: -14, dy: 16, anchor: "end" },
  },

  // Americas - far apart, simpler placement
  "New York · USA": {
    lines: ["NEW YORK"],
    desktop: { dx: 22, dy: -6, anchor: "start" },    // E into Atlantic
    mobile:  { dx: 20, dy: -6, anchor: "start" },
  },
  "Houston · USA": {
    lines: ["HOUSTON"],
    desktop: { dx: 0, dy: 18, anchor: "middle" },    // S into Gulf of Mexico
    mobile:  { dx: 0, dy: 18, anchor: "middle" },
  },
  "Vancouver · CA": {
    lines: ["VANCOUVER"],
    desktop: { dx: -22, dy: -8, anchor: "end" },     // NW into Pacific
    mobile:  { dx: -20, dy: -8, anchor: "end" },
  },
  "Santos · BR": {
    lines: ["SANTOS"],
    desktop: { dx: 22, dy: 8, anchor: "start" },     // E into Atlantic
    mobile:  { dx: 20, dy: 8, anchor: "start" },
  },
};

const WIDTH = 1000;
const HEIGHT_DESKTOP = 540;
// Mobile starts fully zoomed-out (world fits viewport). User pinches or
// taps the +/− buttons to zoom in. Vertical footprint is ~30% shorter
// than the desktop map so the section doesn't dominate the phone screen.
const HEIGHT_MOBILE = 380;
const MOBILE_VIEWBOX = `0 0 ${WIDTH} ${HEIGHT_MOBILE}`;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.6;

// Bowed quadratic between two points - a sea-route arc.
const curvedPath = (x1, y1, x2, y2) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.hypot(dx, dy) || 1;
  const nx = dy / dist;
  const ny = -dx / dist;
  const bow = Math.min(dist * 0.32, 150);
  const cx = mx + nx * bow;
  const cy = my + ny * bow;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
};

const labelConfig = (port, isMobile) => {
  const city = port.name.split("·")[0].trim().toUpperCase();
  const layout = PORT_LABELS[port.name];
  if (!layout) {
    return {
      lines: [city],
      dx: 0,
      dy: isMobile ? -14 : -16,
      anchor: "middle",
    };
  }
  return {
    lines: layout.lines,
    ...(isMobile ? layout.mobile : layout.desktop),
  };
};

const leaderX = (x, anchor) => {
  if (anchor === "start") return x - 6;
  if (anchor === "end") return x + 6;
  return x;
};

export default function WorldMap() {
  const [geo, setGeo] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [activeRegion, setActiveRegion] = useState(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);   // 1 = world fits viewport
  const [showHint, setShowHint] = useState(true);
  const scrollerRef = useRef(null);
  const pinchRef = useRef(0);

  const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
  const zoomIn = () => {
    setShowHint(false);
    setZoom((z) => clampZoom(z + ZOOM_STEP));
  };
  const zoomOut = () => setZoom((z) => clampZoom(z - ZOOM_STEP));
  const zoomFit = () => setZoom(MIN_ZOOM);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = Math.hypot(dx, dy) || 1;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / pinchRef.current;
      pinchRef.current = dist;
      setShowHint(false);
      setZoom((z) => clampZoom(z * ratio));
      // Prevent the page from scrolling while pinching the map
      if (e.cancelable) e.preventDefault();
    }
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch("/world-110m.json")
      .then((r) => r.json())
      .then((topo) => { if (!cancelled) setGeo(feature(topo, topo.objects.countries)); })
      .catch((err) => console.error("WorldMap data load failed", err));
    return () => { cancelled = true; };
  }, []);

  const HEIGHT = isMobile ? HEIGHT_MOBILE : HEIGHT_DESKTOP;
  const viewBox = isMobile ? MOBILE_VIEWBOX : `0 0 ${WIDTH} ${HEIGHT}`;

  const { projection, pathGen } = useMemo(() => {
    // Same scale on mobile + desktop - both show the whole world now.
    const scale = isMobile ? 165 : 175;
    const translateY = HEIGHT / 2 + 10;
    const proj = geoNaturalEarth1().scale(scale).translate([WIDTH / 2, translateY]);
    return { projection: proj, pathGen: geoPath(proj) };
  }, [isMobile, HEIGHT]);

  const hqProj = useMemo(() => {
    const p = projection([HQ.lon, HQ.lat]);
    return p ? { x: p[0], y: p[1] } : null;
  }, [projection]);

  const portProjs = useMemo(() => PORTS.map((p) => {
    const xy = projection([p.lon, p.lat]);
    return xy ? { ...p, x: xy[0], y: xy[1] } : null;
  }).filter(Boolean), [projection]);

  // On mobile, when zoomed in (zoom > 1) the SVG overflows the container
  // horizontally - auto-scroll so Mundra (or the active region's centroid)
  // is centred. At zoom = 1 the world fits the viewport so there's nothing
  // to scroll. Re-runs when the user zooms or changes filter.
  useEffect(() => {
    if (!isMobile || !scrollerRef.current || !hqProj) return;
    if (zoom <= MIN_ZOOM + 0.001) return;
    const node = scrollerRef.current;
    const renderWidth = node.scrollWidth;
    const visibleWidth = node.clientWidth;
    let centerX = (hqProj.x / WIDTH) * renderWidth;
    if (activeRegion) {
      const regional = portProjs.filter((p) => p.region === activeRegion);
      if (regional.length > 0) {
        const meanX = regional.reduce((s, p) => s + p.x, 0) / regional.length;
        const midX = (meanX + hqProj.x) / 2;
        centerX = (midX / WIDTH) * renderWidth;
      }
    }
    const target = Math.max(0, centerX - visibleWidth / 2);
    node.scrollTo({ left: target, behavior: "smooth" });
  }, [isMobile, activeRegion, hqProj, portProjs, zoom]);

  // Auto-fade the zoom hint after 6 seconds
  useEffect(() => {
    if (!isMobile || !showHint) return undefined;
    const id = window.setTimeout(() => setShowHint(false), 6000);
    return () => window.clearTimeout(id);
  }, [isMobile, showHint]);

  return (
    <div className="relative">
      {/* Region toggle - sits above the map */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="folio mr-2">FILTER:</span>
        {[
          { id: null,        label: "All routes",      swatch: "var(--ink)"        },
          { id: "gulf",      label: "Persian Gulf",    swatch: REGION_INK.gulf     },
          { id: "africa",    label: "Africa & Levant", swatch: REGION_INK.africa   },
          { id: "asia",      label: "Asia Pacific",    swatch: REGION_INK.asia     },
          { id: "europe",    label: "Europe",          swatch: REGION_INK.europe   },
          { id: "americas",  label: "Americas",        swatch: REGION_INK.americas },
        ].map((opt) => {
          const active = activeRegion === opt.id;
          return (
            <button
              key={opt.id ?? "all"}
              onClick={() => setActiveRegion(opt.id)}
              className="folio-chip"
              style={{
                borderColor: active ? opt.swatch : "var(--rule)",
                color: active ? opt.swatch : "var(--ink-mute)",
                background: active ? "rgba(255,255,255,0.6)" : "transparent",
                cursor: "pointer",
              }}
            >
              <span style={{
                display: "inline-block", width: 8, height: 8,
                background: opt.swatch, borderRadius: 2, marginRight: 4,
              }} />
              {opt.label}
            </button>
          );
        })}
        <span className="ml-auto status-dot status-dot--live" title="Routes live" />
      </div>

      {/* Folio map frame - double border like a printed plate */}
      <div
        className="relative trade-map-frame"
        style={{
          marginInline: isMobile ? -10 : 0,
          padding: isMobile ? 8 : 14,
          border: "1px solid var(--ink)",
          background: "var(--paper-1)",
        }}
      >
        <div
          className="trade-map-inner"
          style={{ border: "1px solid var(--brass-rule)", padding: isMobile ? 4 : 6, position: "relative" }}
        >
          <div
            ref={scrollerRef}
            className={`trade-map-scroller ${isMobile ? "is-mobile" : ""}`}
            style={{
              overflowX: isMobile ? "auto" : "visible",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              position: "relative",
              touchAction: isMobile ? "pan-x pinch-zoom" : "auto",
            }}
            onTouchStart={isMobile ? onTouchStart : undefined}
            onTouchMove={isMobile ? onTouchMove : undefined}
          >
          <svg
            viewBox={viewBox}
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Vilora Impex active trade routes"
            className="trade-map-svg"
            style={{
              // Mobile: SVG width scales with zoom (1× = 100% = world fits;
              // higher zoom overflows the scroller horizontally).
              width: isMobile ? `${zoom * 100}%` : "100%",
              minWidth: isMobile ? `${zoom * 100}%` : 0,
              height: "auto",
              display: "block",
              background: "var(--bone)",
              transition: "width 320ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <defs>
              {/* Stipple / paper grain filter applied selectively to land */}
              <filter id="stipple">
                <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
                <feColorMatrix values="0 0 0 0 0.10  0 0 0 0 0.12  0 0 0 0 0.21  0 0 0 0.18 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
              {/* Pulsing ring around HQ */}
              <radialGradient id="hqRing" cx="50%" cy="50%" r="50%">
                <stop offset="50%" stopColor="rgba(200,67,31,0)" />
                <stop offset="65%" stopColor="rgba(200,67,31,0.4)" />
                <stop offset="100%" stopColor="rgba(200,67,31,0)" />
              </radialGradient>
              {/* Pattern: dotted longitude grid */}
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="0" cy="0" r="0.4" fill="rgba(26,31,54,0.25)" />
              </pattern>
            </defs>

            {/* Sea - dotted grid as if longitude/latitude marks */}
            <rect x="0" y="0" width={WIDTH} height={HEIGHT} fill="url(#grid)" />

            {/* Land - parchment fill + sepia hairline coast */}
            {geo && (
              <g>
                {geo.features.map((f, i) => (
                  <path
                    key={f.id || i}
                    d={pathGen(f) || ""}
                    fill="#ECE3CE"
                    stroke="#A8843D"
                    strokeWidth={0.5}
                    strokeOpacity={0.55}
                  />
                ))}
                {/* Stipple texture - overlay only inside land shapes */}
                <g filter="url(#stipple)" opacity="0.45">
                  {geo.features.map((f, i) => (
                    <path
                      key={`stipple-${f.id || i}`}
                      d={pathGen(f) || ""}
                      fill="#A8843D"
                      stroke="none"
                    />
                  ))}
                </g>
              </g>
            )}

            {/* Trade routes - bowed sea lanes, dashed */}
            {hqProj && portProjs.map((p, i) => {
              const dim = activeRegion && p.region !== activeRegion;
              const ink = REGION_INK[p.region];
              const allRoutes = !activeRegion;
              const pathId = `vilora-route-${i}`;
              const routePath = curvedPath(hqProj.x, hqProj.y, p.x, p.y);
              return (
                <g key={`lane-${i}`}>
                  <path
                    id={pathId}
                    d={routePath}
                    fill="none"
                    stroke={ink}
                    strokeWidth={dim ? (isMobile ? 0.9 : 0.5) : allRoutes ? (isMobile ? 2.3 : 1.05) : (isMobile ? 2.8 : 1.5)}
                    strokeOpacity={dim ? 0.12 : allRoutes ? 0.7 : 0.9}
                    strokeDasharray={isMobile ? "4.5 10" : "2.2 7"}
                    strokeLinecap="round"
                    style={{ transition: "stroke-opacity 400ms ease, stroke-width 400ms ease" }}
                  />
                  <circle
                    className="route-cargo-dot"
                    r={isMobile ? (allRoutes ? 4.1 : 4.8) : allRoutes ? 2.1 : 2.6}
                    fill={ink}
                    stroke="var(--bone)"
                    strokeWidth={isMobile ? 1.4 : 0.9}
                    opacity={dim ? 0 : allRoutes ? 0.74 : 0.95}
                  >
                    <animateMotion
                      dur={`${5.2 + (i % 5) * 0.45}s`}
                      begin={`${(i % 4) * 0.35}s`}
                      repeatCount="indefinite"
                      keyPoints="0;1;0"
                      keyTimes="0;0.5;1"
                      calcMode="linear"
                    >
                      <mpath href={`#${pathId}`} xlinkHref={`#${pathId}`} />
                    </animateMotion>
                  </circle>
                </g>
              );
            })}

            {/* Port markers */}
            {portProjs.map((p, i) => {
              const dim = activeRegion && p.region !== activeRegion;
              const ink = REGION_INK[p.region];
              return (
                <g
                  key={`port-${i}`}
                  transform={`translate(${p.x}, ${p.y})`}
                  style={{ transition: "opacity 400ms ease", opacity: dim ? 0.25 : 1 }}
                >
                  {/* Concentric mark - slightly smaller so dense clusters
                      (Persian Gulf especially) don't visually merge. */}
                  <circle r={isMobile ? 6 : 4.2} fill="none" stroke={ink} strokeWidth={isMobile ? 0.9 : 0.6} strokeOpacity={0.55} />
                  <rect
                    x={isMobile ? -2.8 : -1.8}
                    y={isMobile ? -2.8 : -1.8}
                    width={isMobile ? 5.6 : 3.6}
                    height={isMobile ? 5.6 : 3.6}
                    fill={ink}
                  />
                </g>
              );
            })}

            {/* Port labels - offset callouts keep clustered routes readable */}
            {portProjs.map((p, i) => {
              const dim = activeRegion && p.region !== activeRegion;
              const ink = REGION_INK[p.region];
              const label = labelConfig(p, isMobile);
              const lx = p.x + label.dx;
              const ly = p.y + label.dy;
              const visible = !dim;
              return (
                <g
                  key={`port-label-${i}`}
                  opacity={visible ? 1 : 0}
                  style={{ transition: "opacity 320ms ease" }}
                >
                  <path
                    d={`M ${p.x.toFixed(2)} ${p.y.toFixed(2)} L ${leaderX(lx, label.anchor).toFixed(2)} ${(ly - 3).toFixed(2)}`}
                    fill="none"
                    stroke={ink}
                    strokeWidth={isMobile ? 0.8 : 0.45}
                    strokeOpacity={0.45}
                    strokeDasharray="1.5 3"
                  />
                  <text
                    x={lx}
                    y={ly}
                    textAnchor={label.anchor}
                    fontSize={isMobile ? 12 : 7.4}
                    fontFamily="'JetBrains Mono', monospace"
                    fill="var(--ink)"
                    style={{
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      paintOrder: "stroke",
                      stroke: "rgba(244,239,227,0.92)",
                      strokeWidth: isMobile ? 4.2 : 3.2,
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                    }}
                  >
                    {label.lines.map((line, lineIndex) => (
                      <tspan key={line} x={lx} dy={lineIndex === 0 ? 0 : isMobile ? 14 : 9}>
                        {line}
                      </tspan>
                    ))}
                  </text>
                </g>
              );
            })}

            {/* HQ - Mundra. Smaller pulsing ring (max r=20) so neighbouring
                ports like Muscat sit cleanly outside the indicator. */}
            {hqProj && (
              <g transform={`translate(${hqProj.x}, ${hqProj.y})`}>
                <circle r={18} fill="url(#hqRing)">
                  <animate attributeName="r" values="12;20;12" dur="3.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.7;0.1;0.7" dur="3.6s" repeatCount="indefinite" />
                </circle>
                <circle r={9} fill="none" stroke="#A8843D" strokeWidth={0.8} />
                <circle r={4.2} fill="#C8431F" stroke="#1A1F36" strokeWidth={0.6} />
                {/* Mundra label sits above the max pulse ring */}
                <text
                  y={isMobile ? -32 : -26}
                  textAnchor="middle"
                  fontSize={isMobile ? 13 : 10}
                  fontFamily="'Cormorant Garamond', serif"
                  fontStyle="italic"
                  fill="var(--ink)"
                  style={{
                    paintOrder: "stroke",
                    stroke: "rgba(244,239,227,0.95)",
                    strokeWidth: isMobile ? 4 : 3,
                    strokeLinejoin: "round",
                  }}
                >
                  Mundra · Gujarat
                </text>
              </g>
            )}

            {/* Compass rose - top-right */}
            <g transform={`translate(${WIDTH - 70}, 70)`}>
              <circle r={28} fill="none" stroke="var(--brass)" strokeWidth={0.6} strokeOpacity={0.7} />
              <circle r={18} fill="none" stroke="var(--brass)" strokeWidth={0.4} strokeOpacity={0.5} />
              {/* Cardinal arms */}
              <path d="M0 -28 L0 28 M-28 0 L28 0" stroke="var(--brass)" strokeWidth={0.4} strokeOpacity={0.6} />
              {/* North needle */}
              <path d="M0 -26 L4 0 L0 4 L-4 0 Z" fill="var(--vermilion)" stroke="var(--ink)" strokeWidth={0.4} />
              {/* South needle */}
              <path d="M0 26 L4 0 L0 -4 L-4 0 Z" fill="var(--bone)" stroke="var(--ink)" strokeWidth={0.4} />
              <text y={-32} textAnchor="middle" fontSize="9" fontFamily="'Cormorant Garamond', serif" fontStyle="italic" fill="var(--ink)">N</text>
              <text y={38} textAnchor="middle" fontSize="9" fontFamily="'Cormorant Garamond', serif" fontStyle="italic" fill="var(--ink)">S</text>
              <text x={36} y={3} textAnchor="middle" fontSize="9" fontFamily="'Cormorant Garamond', serif" fontStyle="italic" fill="var(--ink)">E</text>
              <text x={-36} y={3} textAnchor="middle" fontSize="9" fontFamily="'Cormorant Garamond', serif" fontStyle="italic" fill="var(--ink)">W</text>
            </g>

            {/* Scale bar - bottom-left */}
            <g transform={`translate(40, ${HEIGHT - 38})`}>
              <text fontFamily="'JetBrains Mono', monospace" fontSize="8" fill="var(--ink-mute)" style={{ letterSpacing: "0.2em" }}>
                NAUTICAL MILES
              </text>
              <g transform="translate(0, 8)">
                <rect x="0" y="0" width="40" height="6" fill="var(--ink)" />
                <rect x="40" y="0" width="40" height="6" fill="var(--bone)" stroke="var(--ink)" strokeWidth="0.6" />
                <rect x="80" y="0" width="40" height="6" fill="var(--ink)" />
                <text x="0" y="20" fontSize="8" fontFamily="'JetBrains Mono', monospace" fill="var(--ink-mute)">0</text>
                <text x="60" y="20" fontSize="8" fontFamily="'JetBrains Mono', monospace" fill="var(--ink-mute)" textAnchor="middle">1500</text>
                <text x="120" y="20" fontSize="8" fontFamily="'JetBrains Mono', monospace" fill="var(--ink-mute)" textAnchor="end">3000</text>
              </g>
            </g>

            {/* Edition stamp - bottom-right */}
            <g transform={`translate(${WIDTH - 200}, ${HEIGHT - 30})`}>
              <text fontFamily="'JetBrains Mono', monospace" fontSize="8" fill="var(--brass-deep)" style={{ letterSpacing: "0.22em" }}>
                PLATE V - TRADE ROUTES - 2025
              </text>
            </g>
          </svg>
          </div>

          {isMobile && (
            <>
              {/* Zoom controls - vertical stack, top-right corner */}
              <div className="trade-map-zoom" role="group" aria-label="Map zoom">
                <button
                  type="button"
                  className="trade-map-zoom-btn"
                  aria-label="Zoom in"
                  onClick={zoomIn}
                  disabled={zoom >= MAX_ZOOM}
                >
                  +
                </button>
                <button
                  type="button"
                  className="trade-map-zoom-btn"
                  aria-label="Zoom out"
                  onClick={zoomOut}
                  disabled={zoom <= MIN_ZOOM}
                >
                  −
                </button>
                <button
                  type="button"
                  className="trade-map-zoom-btn trade-map-zoom-fit"
                  aria-label="Fit world"
                  onClick={zoomFit}
                  title="Fit world"
                  disabled={zoom === MIN_ZOOM}
                >
                  ⤢
                </button>
              </div>

              {/* Zoom indicator - fades after first interaction or 6s */}
              {showHint && zoom === MIN_ZOOM && (
                <div className="trade-map-zoom-hint mono" aria-hidden="true">
                  <span className="trade-map-zoom-hint-plus">＋</span>
                  <span>Pinch or tap + to zoom in</span>
                </div>
              )}

              {/* Tiny zoom level read-out */}
              <div className="trade-map-zoom-level mono" aria-live="polite">
                {Math.round(zoom * 100)}%
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}
